/**
 * ResetPassword.jsx (iter148) — Public page reached via the email link.
 *
 * URL: /reset-password?token=...
 * Submits {token, new_password} → backend validates token + sets pw.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Barcode, Eye, EyeSlash, CheckCircle, Warning } from '@phosphor-icons/react';
import ThemeToggleButton from '../components/ThemeToggleButton';

const API = process.env.REACT_APP_BACKEND_URL;

const validate = (pw) => {
  if (!pw || pw.length < 8) return 'Must be at least 8 characters.';
  if (!/[A-Za-z]/.test(pw)) return 'Must include a letter.';
  if (!/\d/.test(pw)) return 'Must include a digit.';
  return null;
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) setError('This link is missing its reset token. Use the link from your email.');
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const v = validate(pw);
    if (v) { setError(v); return; }
    if (pw !== pw2) { setError('Passwords don\u2019t match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 relative">
      <div className="absolute top-4 right-4 z-10"><ThemeToggleButton /></div>

      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-md bg-brand flex items-center justify-center">
            <Barcode weight="bold" className="text-brand-foreground" size={24} />
          </div>
          <span className="font-bold text-2xl tracking-tight text-foreground">STOCKARIS</span>
        </div>

        <div className="bg-card border border-border rounded-lg p-8 shadow-sm" data-testid="reset-password-card">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle size={24} weight="fill" className="text-emerald-700" />
              </div>
              <h1 className="text-xl font-semibold mb-2">Password updated</h1>
              <p className="text-sm text-muted-foreground mb-6">
                You can now sign in with your new password. Redirecting to sign in…
              </p>
              <Link to="/login" className="text-sm text-brand font-semibold hover:underline">Sign in now</Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold mb-1 text-foreground tracking-tight">Set a new password</h1>
              <p className="text-muted-foreground mb-6 text-sm">
                At least 8 characters, with a letter and a digit.
              </p>

              {error && (
                <div data-testid="reset-error" className="mb-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md flex items-start gap-2">
                  <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="pw" className="overline">New password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="pw"
                      type={show ? 'text' : 'password'}
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      placeholder="••••••••"
                      data-testid="reset-password-input"
                      autoFocus
                      required
                    />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="reset-show-toggle">
                      {show ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="pw2" className="overline">Confirm new password</Label>
                  <Input
                    id="pw2"
                    type={show ? 'text' : 'password'}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    placeholder="••••••••"
                    data-testid="reset-password-confirm"
                    className="mt-1"
                    required
                  />
                </div>
                <Button type="submit" data-testid="reset-submit" disabled={loading || !token} className="w-full font-semibold py-3">
                  {loading ? 'Saving…' : 'Update password'}
                </Button>
              </form>

              <Link to="/login" className="mt-6 block text-sm text-muted-foreground hover:text-foreground" data-testid="reset-back-to-login">
                Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
